import { useMemo, useState } from 'react';
import clsx from 'clsx';
import { Check, X } from '@/components/ui/icons';
import { useDb, useMe } from '@/lib/store';
import { Avatar } from '@/components/ui/Avatar';
import { MenuSearch, useListNav } from '@/components/ui/Menu';

/** Multi-select people menu (assignees, watchers, people custom fields). */
export function PeopleMenu({
  selected,
  onChange,
  single = false,
  title = 'Assignees',
}: {
  selected: string[];
  onChange: (ids: string[]) => void;
  single?: boolean;
  title?: string;
}) {
  const users = useDb((db) => db.users);
  const me = useMe();
  const [q, setQ] = useState('');
  const options = useMemo(() => {
    const ql = q.trim().toLowerCase();
    const all = Object.values(users).filter((u) => !ql || u.name.toLowerCase().includes(ql) || u.email.toLowerCase().includes(ql));
    // me first, then selected, then alphabetical
    return all.sort((a, b) => (a.id === me ? -1 : b.id === me ? 1 : 0) || Number(selected.includes(b.id)) - Number(selected.includes(a.id)) || a.name.localeCompare(b.name));
  }, [users, q, me, selected]);
  const toggle = (id: string) => {
    if (single) onChange(selected.includes(id) ? [] : [id]);
    else onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  };
  const nav = useListNav(options.length, (i) => toggle(options[i].id));
  return (
    <div className="menu" style={{ width: 280 }} onKeyDown={nav.onKeyDown}>
      <MenuSearch value={q} onChange={setQ} placeholder="Search or enter email..." />
      <div className="menu-label">{title}</div>
      {options.map((u, i) => {
        const on = selected.includes(u.id);
        return (
          <button key={u.id} className={clsx('menu-item', { active: nav.index === i })} onMouseEnter={() => nav.setIndex(i)} onClick={() => toggle(u.id)}>
            <Avatar userId={u.id} size={24} showPresence />
            <span className="menu-item-label truncate">{u.id === me ? 'Me' : u.name}</span>
            {on ? (
              <span className="people-remove" title="Remove">
                <X size={14} />
              </span>
            ) : null}
            {on && <Check size={14} className="faint" />}
          </button>
        );
      })}
      {options.length === 0 && <div className="menu-empty">No people found</div>}
    </div>
  );
}
